<template>
  <v-card rounded="0" class="mb-5">
    <div class="hcm-card">
      <div
        v-if="cardImage"
        :style="'background-image: url(' + cardImage + ')'"
        class="card-image"
      ></div>

      <div class="card-content">
        <div v-if="cardInfo" class="item-info">
          {{ cardInfo }}
        </div>

        <v-card-title
          v-if="cardTitle"
          :class="{ 'collection-title': !cardInfo }"
        >
          <div v-if="titleLink">
            <a
              v-if="externalResource"
              :href="resourceUrl"
              v-html="$options.filters.highlightSearch(cardTitle, query())"
              @click="trackLink()"
            ></a>
            <router-link
              v-else
              :to="resourceUrl"
              v-html="$options.filters.highlightSearch(cardTitle, query())"
            ></router-link>
          </div>
          <div
            v-else
            v-html="$options.filters.highlightSearch(cardTitle, query())"
          ></div>
        </v-card-title>

        <v-card-text
          :class="{ 'clickable-text': externalResource }"
          @click="externalResource ? openLink() : null"
        >
          <slot>
            <span
              v-if="cardText"
              v-html="highlightHtmlSearch(formatCardText(cardText), query())"
            ></span>
          </slot>
          <slot name="custom-actions"></slot>
        </v-card-text>

        <v-card-actions
          v-if="hasSource || hasShare"
          class="card-actions d-flex"
        >
          <div v-if="hasSource" class="card-source d-flex">
            <img
              v-if="externalResource"
              :src="'https://www.google.com/s2/favicons?domain=' + resourceUrl"
              onerror="this.style.display='none';"
              class="source-icon"
            />
            <img
              v-else
              :src="
                'https://www.google.com/s2/favicons?domain=' + cardSourceUrl
              "
              onerror="this.style.display='none';"
              class="source-icon"
            />
            <span>{{ cardSourceAuthor }}</span>
          </div>
          <v-spacer></v-spacer>
          <div v-if="hasShare">
            <v-icon class="share-icon ml-auto" @click="shareResource()"
              >mdi-share-variant</v-icon
            >
          </div>
        </v-card-actions>
      </div>
    </div>
  </v-card>
</template>

<script>
import marked from "marked";
import { without, uniq } from "lodash";
import { stopWords } from "../utils/contentfulParser.js";
import Mark from "mark.js";

export default {
  props: {
    resource: {
      type: Object,
      required: true
    },
    cardImage: {
      type: String,
      required: false
    },
    cardInfo: {
      type: String,
      required: false
    },
    cardTitle: {
      type: String,
      required: false
    },
    titleLink: {
      type: Boolean,
      required: false,
      default: true
    },
    resourceUrl: {
      type: String,
      required: false
    },
    externalResource: {
      type: Boolean,
      required: true
    },
    cardText: {
      type: String,
      required: false
    },
    hasSource: {
      type: Boolean,
      required: true
    },
    cardSourceUrl: {
      type: String,
      required: false
    },
    cardSourceAuthor: {
      type: String,
      required: false
    },
    shareTag: {
      type: String,
      required: false
    },
    hasShare: {
      type: Boolean,
      required: true
    },
    shareTitle: {
      type: String,
      required: false
    },
    shareUrl: {
      type: String,
      required: false
    }
  },
  filters: {
    highlightSearch(val, q) {
      if (q) {
        // want to highlight entire phrase if it exists; otherwise, highlight individual words
        // need to ensure we don't double-highlight -- or maybe it doesn't matter??
        // in theory, we can add the markup to the markdown text and it should work...
        // strip stop words from the words to check for, but also check for the full phrase
        let checks = uniq([
          q.toLowerCase(),
          ...without(q.toLowerCase().split(/[\W/]+/), ...stopWords)
        ]);

        // we need to mangle the text to insert html tags, but we don't want accidentally mangle
        // any html tags themselves, so we construct the text as an html document and look at the
        // text children only
        let newVal = val;
        let parser = new DOMParser();
        let doc = parser.parseFromString(newVal, "text/html");
        let nodes = doc.querySelectorAll("*");

        checks.forEach(check => {
          // check to see if there is already are already html tags
          // console.log("Checking: ", check);

          for (let i = 0; i < nodes.length; i++) {
            if (
              nodes[i].nodeName !== "HTML" &&
              nodes[i].nodeName !== "HEAD" &&
              !(
                nodes[i].nodeName === "SPAN" &&
                nodes[i].classList.contains("highlighted-search-text")
              )
            ) {
              // console.log(nodes[i]);
              // we should be able to look at just the text childNodes and replace any
              for (let j = 0; j < nodes[i].childNodes.length; j++) {
                // nodeType === 3 -> text nodes only
                if (nodes[i].childNodes[j].nodeType === 3) {
                  let txt = nodes[i].childNodes[j].textContent;

                  // we work backwards because we'll modify the string as we work through it
                  // and don't want to have to figure out the how the last insertion needs
                  // to be accounted for.
                  let idx = txt.toLowerCase().lastIndexOf(check);

                  while (idx !== -1) {
                    txt =
                      txt.substring(0, idx) +
                      '<span class="highlighted-search-text">' +
                      txt.substring(idx, idx + check.length) +
                      "</span>" +
                      txt.substring(idx + check.length, txt.length);

                    nodes[i].childNodes[j].textContent = txt;

                    idx = txt.toLowerCase().lastIndexOf(check, idx - 1);
                  }
                }
              }
            }
          }
        });
        newVal = nodes.item(2).textContent;
        // console.log(newVal);

        return newVal;
      } else {
        return val;
      }
    }
  },
  methods: {
    highlightHtmlSearch(val, q) {
      if (q) {
        let checks = uniq([
          q.toLowerCase(),
          ...without(q.toLowerCase().split(/[\W/]+/), ...stopWords)
        ]);

        let newVal = val;
        let parser = new DOMParser();
        let doc = parser.parseFromString(newVal, "text/html");
        // let nodes = doc.querySelectorAll("*");

        let markInstance = new Mark(doc.querySelector("body"));
        let options = {
          element: "span",
          className: "highlighted-search-text",
          separateWordSearch: false
        };

        markInstance.mark(checks, options);

        // console.log("qqq", doc.querySelector("body").innerHTML);

        return doc.querySelector("body").innerHTML;
      }
      return val;
    },
    query() {
      if (this.$route.query.q) {
        return this.$route.query.q;
      } else {
        return undefined;
      }
    },
    openLink() {
      this.trackLink();
      window.open(this.resourceUrl, "_self");
    },
    trackLink() {
      this.$ga.event({
        eventCategory: "News Item: " + this.cardTitle,
        eventAction: "View"
      });
    },
    shareResource() {
      console.log("Share");
      this.$ga.event({
        eventCategory: `Share ${this.shareTag}: ${this.shareTitle}`,
        eventAction: "Tap"
      });

      const subject = `I would like to share information about a heart condition, hypertrophic cardiomyopathy (HCM), with you.`;

      // prettier-ignore
      const body = `Dear family or friend,

I want to share this ${this.shareTag} with you, which includes important information about hypertrophic cardiomyopathy (HCM).
Please view online via the links below:

${this.shareTitle}:

${this.shareUrl}`;

      window.open(
        `mailto:?subject=${encodeURIComponent(
          subject
        )}&body=${encodeURIComponent(body)}`
      );
    },
    formatCardText(cardText) {
      let r = marked(cardText);
      return r;
    }
  }
};
</script>

<style scoped>
.v-card {
  margin: 19.5px 0px;
}

.v-card__text {
  overflow-wrap: break-word;
  letter-spacing: normal;
  font-weight: 400;
  font-size: 15.6px;
  color: rgb(0, 0, 0) !important;

  padding: 20px 30px !important;
}

.v-card__title {
  overflow-wrap: break-word;
  word-break: break-word;
  letter-spacing: normal;
  font-weight: 700;
  font-size: 22px;
  line-height: 22px;

  padding: 0px 30px;
}

.v-card__actions {
  margin-top: 20px;
  padding: 15px 30px;
  border-top-width: 1px;
}

@media (min-width: 0px) {
  .hcm-card {
    display: flex;
    flex-wrap: nowrap;
    flex-direction: column;
    justify-content: space-between;
  }
  .card-image {
    height: 200px;

    background-position: center;
    background-size: cover;
  }
}

@media (min-width: 800px) {
  .hcm-card {
    display: flex;
    flex-wrap: nowrap;
    flex-direction: row;
    justify-content: space-between;
  }
  .card-image {
    -webkit-box-flex: 2;
    flex: 2;
    height: auto;

    background-position: center;
    background-size: cover;
  }
  .card-content {
    -webkit-box-flex: 3;
    flex: 3;
  }
}

.item-info {
  padding: 20px 30px;
  font-size: 13px;
  font-weight: 400;
  line-height: 13px;
  color: rgb(90, 101, 102);
  text-align: right;
}

.collection-title {
  padding-top: 30px !important;
}

.clickable-text {
  cursor: pointer !important;
}

::v-deep a {
  text-decoration-line: none !important;
  word-break: break-word;
}

::v-deep p {
  margin-bottom: 0px !important;
}

::v-deep p + p {
  margin-top: 16px !important;
}

.card-actions {
  color: rgb(18, 102, 173);
  align-items: center;
  border-top: 1px solid #ccc;
}

::v-deep .highlighted-search-text {
  background: #a0c2de;
  border-radius: 5px;
  font-size: inherit;
}

.card-source {
  font-size: 13px;
  font-weight: 400;
  line-height: 13px;
  align-items: center;
}

.source-icon {
  margin-right: 10px;
}

.share-icon {
  color: rgb(18, 102, 173) !important;
}

::v-deep li {
  margin-top: 14px !important;
}

::v-deep img {
  max-width: 100% !important;
}
</style>
